const {
    S3Client,
    CreateMultipartUploadCommand,
    UploadPartCommand,
    CompleteMultipartUploadCommand,
    AbortMultipartUploadCommand,
} = require('@aws-sdk/client-s3');

const { DEFAULT_CONFIG, VALIDATION_RESULT } = require('./constants');
const TaskManager = require('./services/task-manager');
const ProgressTracker = require('./services/progress-tracker');
const CacheService = require('./services/cache-service');
const ErrorHandler = require('./services/error-handler');
const ConcurrentExecutor = require('./services/concurrent-executor');
const { calculateChunkSize } = require('./utils/chunk-calculator');
const { prepareChunkData } = require('./utils/data-preparer');

const tool = require('../tool');
const dataService = require('../dataService');
const logger = require('../logger');

const noop = tool.noop;
const isFunction = tool.isFunction;
const replaceAWSPath = tool.replaceAWSPath;
const hasResourceCallback = tool.hasResourceCallback;

const taskManager = new TaskManager();

/**
 * 多片上传管理器类
 */
class AwsMultipartUploader {
    constructor(file, options, config = {}) {
        this.file = file;
        this.options = Object.assign(
            {
                beforeUpload: noop,
                success: noop,
                error: noop,
                progress: noop,
            },
            options
        );
        this.config = Object.assign({}, DEFAULT_CONFIG, config);

        this.abortController = new AbortController();
        this.s3Client = null;
        this.uploadInfo = null;
        this.uploadRecord = null;
        this.fileMD5 = null;

        // 生成唯一的任务ID，用于本地任务管理
        this.taskId = Math.random().toString(36).substring(2, 15);

        // 计算分片配置
        const { chunkSize, numParts } = calculateChunkSize(file.size);
        this.chunkSize = chunkSize;
        this.numParts = numParts;

        // 初始化服务类
        this.cacheService = new CacheService();
        this.progressTracker = new ProgressTracker(file.size, this.options.progress);

        logger.log('[AWS Multipart] Uploader initialized', {
            fileSize: file.size,
            fileName: file.name,
            chunkSize: this.chunkSize,
            numParts: this.numParts,
        });
    }

    /**
     * 初始化 AWS SDK
     */
    async initSDK(region) {
        const data = await dataService.assumerole(region);
        const authConfig = {
            region: region,
            credentials: {
                accessKeyId: data.Credentials.AccessKeyId,
                secretAccessKey: data.Credentials.SecretAccessKey,
                sessionToken: data.Credentials.SessionToken,
            },
        };
        // 检查是否需要自定义 endpoint
        if (this.uploadInfo && parseInt(this.uploadInfo.enable_new_bucket, 10) === 1 && this.uploadInfo.aws_url) {
            const customEndpoint = tool.getURLOriginFromURLString(this.uploadInfo.aws_url);
            if (customEndpoint) {
                authConfig.endpoint = customEndpoint;
                authConfig.forcePathStyle = true; // 必需！
            }
        }

        this.s3Client = new S3Client(authConfig);
    }

    /**
     * 计算文件 MD5
     */
    async calculateFileMD5() {
        try {
            const { default: MD5Calculator } = await import('../md5/md5-calculator.js');
            this.fileMD5 = await MD5Calculator.calculateFileHash(this.file);
            logger.log('[AWS Multipart] MD5 calculated', this.fileMD5);
            return this.fileMD5;
        } catch (error) {
            logger.errorAndLog('[AWS Multipart] MD5 calculation failed:', error);
            this.fileMD5 = null;
            return null;
        }
    }

    /**
     * 查找缓存记录（不验证有效性）
     */
    async findCachedRecord() {
        if (!this.fileMD5 || !this.options.signature?.id) {
            logger.log('[AWS Multipart] No MD5 or teacherId available, cannot find cache');
            return null;
        }

        try {
            const existingRecord = await this.cacheService.findResumeRecord(this.options.signature?.id, this.fileMD5);
            if (!existingRecord) {
                logger.log('[AWS Multipart] No cached record found');
                return null;
            }

            logger.log('[AWS Multipart] Found cached record:', {
                cachedPath: existingRecord.uploadInfo?.path,
                uploadId: existingRecord.uploadId,
                partsCount: existingRecord.parts?.length || 0,
            });

            return existingRecord;
        } catch (error) {
            logger.log('[AWS Multipart] Cache lookup failed:', {
                error: error.message,
                errorType: ErrorHandler.classifyError(error),
            });
            return null; // 缓存查找失败时返回null，不影响主流程
        }
    }

    /**
     * 验证缓存的上传记录
     */
    async validateCachedUpload(record) {
        // 检查必要的参数是否存在
        if (!record.uploadInfo?.bucket || !record.uploadInfo?.path || !record.uploadId) {
            logger.log('[AWS Multipart] Invalid parameters for uploadId validation', {
                bucket: record.uploadInfo?.bucket,
                path: record.uploadInfo?.path,
                uploadId: record.uploadId,
            });
            return 'INVALID';
        }

        return await this.cacheService.validateUploadIdOnS3(
            this.s3Client,
            record.uploadInfo.bucket,
            record.uploadInfo.path,
            record.uploadId,
            this.abortController.signal
        );
    }

    /**
     * 处理验证结果
     */
    async handleValidationResult(validationResult, record) {
        switch (validationResult) {
            case VALIDATION_RESULT.VALID:
                return this.setupResumeUpload(record);

            case VALIDATION_RESULT.INVALID:
                logger.log('[AWS Multipart] Cached upload invalid, clearing cache');
                await this.cacheService.deleteUploadRecord(record.uploadId);
                return null;

            case VALIDATION_RESULT.UNKNOWN:
                logger.log('[AWS Multipart] Cannot verify upload status, keeping cache');
                return null;

            default:
                logger.log('[AWS Multipart] Unknown validation result:', validationResult);
                return null;
        }
    }

    /**
     * 设置断点续传
     */
    setupResumeUpload(record) {
        logger.log('[AWS Multipart] Resume upload validated', {
            uploadId: record.uploadId,
            partsCount: record.parts?.length || 0,
        });

        this.uploadRecord = record;

        // 设置 ProgressTracker 的已完成分片
        const completedParts = (record.parts || []).filter((p) => p.ETag);
        const completedPartNumbers = completedParts.map((p) => p.PartNumber);
        this.progressTracker.setCompletedParts(completedPartNumbers, completedParts);

        return record;
    }

    /**
     * 创建新的多片上传
     */
    async createMultipartUpload() {
        const mimeType = tool.getMimeType(this.file);
        const command = new CreateMultipartUploadCommand({
            Bucket: this.uploadInfo.bucket,
            Key: this.uploadInfo.path,
            ContentType: mimeType,
        });

        const response = await this.s3Client.send(command, {
            abortSignal: this.abortController.signal,
        });
        const s3UploadId = response.UploadId;

        // 创建上传记录
        this.uploadRecord = {
            fileName: this.file.name,
            fileSize: this.file.size,
            lastModified: this.file.lastModified,
            fileMD5: this.fileMD5 || null,
            teacherId: this.options.signature?.id,
            uploadId: s3UploadId,
            chunkSize: this.chunkSize,
            parts: [],
            createdAt: Date.now(),
            uploadInfo: {
                bucket: this.uploadInfo.bucket,
                region: this.uploadInfo.region,
                resource_id: this.uploadInfo.resource_id,
                path: this.uploadInfo.path,
                enable_new_bucket: this.uploadInfo.enable_new_bucket,
                aws_url: this.uploadInfo.aws_url,
            },
        };

        // 只有在有 MD5 时才保存缓存记录
        if (this.fileMD5) {
            try {
                await this.cacheService.saveUploadRecord(this.uploadRecord);
                logger.log('[AWS Multipart] Upload record saved to cache');
            } catch (error) {
                logger.log('[AWS Multipart] Failed to save upload record to cache:', error.message);
            }
        } else {
            logger.log('[AWS Multipart] No MD5, cache disabled for this upload');
        }

        logger.log('[AWS Multipart] New multipart upload created', s3UploadId);
        return s3UploadId;
    }

    /**
     * 更新进度
     */
    updateProgress(partNumber, partSize) {
        if (this.abortController.signal.aborted) {
            return;
        }
        this.progressTracker.updateProgress(partNumber, partSize);
    }

    /**
     * 上传单个分片
     * @param {number} partNumber - 分片编号
     * @param {number} start - 起始字节
     * @param {number} end - 结束字节
     * @param {number} retries - 当前重试次数
     * @param {Object} options - 可选配置
     * @param {boolean} options.force - 是否强制上传（跳过 completedParts 检查，用于补传缺失分片）
     */
    async uploadPart(partNumber, start, end, retries = 0, options = {}) {
        const { force = false } = options;

        if (this.abortController.signal.aborted) {
            throw new Error('Upload cancelled');
        }

        // 非强制模式下，跳过已完成的分片
        if (!force && this.progressTracker.completedParts.has(partNumber)) {
            logger.log(`[AWS Multipart] Part ${partNumber} already uploaded`);
            return;
        }

        try {
            logger.log(
                `[AWS Multipart] ${force ? 'Force uploading' : 'Starting upload for'} Part ${partNumber}, attempt ${
                    retries + 1
                }`,
                {
                    partNumber,
                    start,
                    end,
                    size: end - start,
                    attempt: retries + 1,
                    force,
                }
            );

            const chunk = this.file.slice(start, end);
            const body = await prepareChunkData(chunk);

            const command = new UploadPartCommand({
                Bucket: this.uploadInfo.bucket,
                Key: this.uploadInfo.path,
                PartNumber: partNumber,
                UploadId: this.uploadRecord.uploadId,
                Body: body,
            });

            const response = await this.s3Client.send(command, {
                abortSignal: this.abortController.signal,
            });
            const etag = response.ETag;

            const part = {
                PartNumber: partNumber,
                ETag: etag,
                Size: end - start,
            };

            // 更新内存记录
            if (!this.uploadRecord.parts) {
                this.uploadRecord.parts = [];
            }
            const existingPartIndex = this.uploadRecord.parts.findIndex((p) => p.PartNumber === part.PartNumber);
            if (existingPartIndex >= 0) {
                this.uploadRecord.parts[existingPartIndex] = part;
            } else {
                this.uploadRecord.parts.push(part);
            }

            // 更新缓存（强制模式下跳过缓存更新，因为补传场景不需要）
            if (!force && this.uploadRecord && this.uploadRecord.uploadId && this.fileMD5) {
                try {
                    await this.cacheService.updatePartInfo(this.uploadRecord.uploadId, part);
                } catch (error) {
                    logger.log('[AWS Multipart] Failed to update cache:', error.message);
                }
            }

            // 更新进度（updateProgress 内部已有去重检查，force 模式下也需要更新 uploadedBytes）
            this.updateProgress(partNumber, end - start);

            const percent = parseFloat((this.progressTracker.uploadedBytes / this.file.size).toFixed(4));
            logger.log(`[AWS Multipart] Part ${partNumber} ${force ? 're-' : ''}uploaded successfully`, {
                etag,
                size: end - start,
                progress: Math.round(percent * 100) + '%',
            });
        } catch (error) {
            logger.log(`[AWS Multipart] Part ${partNumber} upload failed`, {
                error: error.message,
                errorType: ErrorHandler.classifyError(error),
                retryCount: retries + 1,
            });
            if (ErrorHandler.shouldRetry(error, retries, this.config.retries) && !this.abortController.signal.aborted) {
                const delay = ErrorHandler.getRetryDelay(retries, this.config.retryDelay);
                logger.log(`[AWS Multipart] Part ${partNumber} upload failed, retrying in ${delay}ms...`, {
                    error: error.message,
                    errorType: ErrorHandler.classifyError(error),
                    retryCount: retries + 1,
                });
                await new Promise((resolve) => setTimeout(resolve, delay));
                return this.uploadPart(partNumber, start, end, retries + 1, options);
            }

            logger.errorAndLog(`[AWS Multipart] Part ${partNumber} upload failed after ${retries + 1} attempts:`, {
                error: error.message,
                errorType: ErrorHandler.classifyError(error),
            });
            throw error;
        }
    }

    /**
     * 完成多片上传
     */
    async completeMultipartUpload() {
        // 直接使用内存中的 parts（内存始终是最可靠的，在 UploadPart 成功后同步更新）
        if (!this.uploadRecord?.parts?.length) {
            logger.errorAndLog('[AWS Multipart] No uploaded parts found in memory');
            throw new Error('No uploaded parts found in memory');
        }

        let parts = this.uploadRecord.parts.map((part) => ({
            ETag: part.ETag,
            PartNumber: part.PartNumber,
        }));

        logger.log('[AWS Multipart] Using memory parts info:', parts.length);

        // 检测缺失的分片
        const missingParts = this.findMissingParts(parts);

        if (missingParts.length > 0) {
            logger.log(
                `[AWS Multipart] Found ${missingParts.length} missing parts: ${missingParts.join(
                    ', '
                )}, re-uploading...`
            );

            // 重新上传缺失的分片
            await this.reuploadMissingParts(missingParts);

            // 重新获取 parts
            parts = this.uploadRecord.parts.map((part) => ({
                ETag: part.ETag,
                PartNumber: part.PartNumber,
            }));

            // 再次验证
            const stillMissing = this.findMissingParts(parts);
            if (stillMissing.length > 0) {
                logger.errorAndLog(`[AWS Multipart] Failed to upload parts after retry: ${stillMissing.join(', ')}`);
                throw new Error(`Failed to upload parts: ${stillMissing.join(', ')}`);
            }

            logger.log('[AWS Multipart] All missing parts re-uploaded successfully');
        }

        parts.sort((a, b) => a.PartNumber - b.PartNumber);

        const command = new CompleteMultipartUploadCommand({
            Bucket: this.uploadInfo.bucket,
            Key: this.uploadInfo.path,
            UploadId: this.uploadRecord.uploadId,
            MultipartUpload: { Parts: parts },
        });

        const response = await this.s3Client.send(command, {
            abortSignal: this.abortController.signal,
        });

        // 清理缓存
        if (this.uploadRecord && this.uploadRecord.uploadId && this.fileMD5) {
            try {
                await this.cacheService.deleteUploadRecord(this.uploadRecord.uploadId);
                logger.log('[AWS Multipart] Cache record cleaned up');
            } catch (error) {
                logger.log('[AWS Multipart] Failed to clean up cache:', error.message);
            }
        }

        logger.log('[AWS Multipart] Upload completed successfully', {
            location: response.Location,
            uploadId: this.uploadRecord?.uploadId || 'unknown',
            totalParts: parts.length,
            expectedParts: this.numParts,
        });

        return response;
    }

    /**
     * 查找缺失的分片
     * @param {Array} parts - 当前已上传的分片列表
     * @returns {number[]} 缺失的分片编号数组
     */
    findMissingParts(parts) {
        const uploadedPartNumbers = new Set(parts.map((p) => p.PartNumber));
        const missing = [];

        for (let i = 1; i <= this.numParts; i++) {
            if (!uploadedPartNumbers.has(i)) {
                missing.push(i);
            }
        }

        return missing;
    }

    /**
     * 重新上传缺失的分片
     * @param {number[]} missingPartNumbers - 缺失的分片编号
     */
    async reuploadMissingParts(missingPartNumbers) {
        logger.log(
            `[AWS Multipart] Re-uploading ${missingPartNumbers.length} missing parts: ${missingPartNumbers.join(', ')}`
        );

        const tasks = missingPartNumbers.map((partNumber) => {
            const start = (partNumber - 1) * this.chunkSize;
            const end = Math.min(start + this.chunkSize, this.file.size);
            if (this.abortController.signal.aborted) {
                throw new Error('Upload cancelled');
            }
            // 强制重新上传，不检查 completedParts
            return () => this.uploadPart(partNumber, start, end, 0, { force: true });
        });

        try {
            await ConcurrentExecutor.run(tasks, this.config.concurrency, {
                failFast: true,
                retries: 0,
            });
        } catch (error) {
            logger.errorAndLog('[AWS Multipart] Re-upload missing parts failed:', error);
            this.abortController.abort();
            throw error;
        }
    }

    /**
     * 创建上传任务列表
     */
    createUploadTasks() {
        const tasks = [];

        for (let partNumber = 1; partNumber <= this.numParts; partNumber++) {
            const start = (partNumber - 1) * this.chunkSize;
            const end = Math.min(start + this.chunkSize, this.file.size);

            tasks.push(() => {
                if (this.abortController.signal.aborted) {
                    throw new Error('Upload cancelled');
                }
                return this.uploadPart(partNumber, start, end);
            });
        }

        return tasks;
    }

    /**
     * 并发上传所有分片
     */
    async uploadAllParts() {
        const tasks = this.createUploadTasks();
        try {
            // 明确禁用 ConcurrentExecutor 的重试机制，使用 uploadPart 内部重试
            await ConcurrentExecutor.run(tasks, this.config.concurrency, {
                failFast: true,
                retries: 0, // 明确设置为 0，避免双重重试
            });
        } catch (error) {
            logger.log('[AWS Multipart] Upload all parts failed:', error);
            // 确保所有任务停止
            this.abortController.abort();
            throw error;
        }
    }

    /**
     * 取消上传
     */
    async cancel() {
        logger.log('[AWS Multipart] Canceling upload...', {
            uploadId: this.uploadRecord?.uploadId,
            fileName: this.file?.name,
        });

        this.abortController.abort();

        if (this.s3Client && this.uploadRecord) {
            try {
                const command = new AbortMultipartUploadCommand({
                    Bucket: this.uploadInfo.bucket,
                    Key: this.uploadInfo.path,
                    UploadId: this.uploadRecord.uploadId,
                });

                await this.s3Client.send(command);

                if (this.fileMD5) {
                    await this.cacheService.deleteUploadRecord(this.uploadRecord.uploadId);
                }

                logger.log('[AWS Multipart] Upload canceled successfully', {
                    uploadId: this.uploadRecord.uploadId,
                });
            } catch (error) {
                logger.errorAndLog('[AWS Multipart] Cancel upload error:', {
                    uploadId: this.uploadRecord?.uploadId,
                    error: error.message,
                });
            }
        } else {
            logger.log('[AWS Multipart] Upload canceled (no AWS cleanup needed)');
        }

        this.cleanupTask();
    }

    /**
     * 清理任务
     */
    cleanupTask() {
        taskManager.removeTask(this.taskId);
    }

    /**
     * 开始上传
     */
    async startUpload() {
        try {
            this.registerTask();
            await this.validateFile();
            await this.prepareUpload();
            await this.executeUpload();
        } catch (error) {
            await this.handleUploadError(error);
        }
    }

    registerTask() {
        taskManager.addTask(this.taskId, {
            uploader: this,
            file: this.file,
            startTime: Date.now(),
            cancelCallback: this.options.onCancel,
        });
    }

    async validateFile() {
        if (this.isFileTooLarge()) {
            this.options.onOverMaxSize && this.options.onOverMaxSize();
            throw new Error('File size exceeds maximum allowed size');
        }

        if (!this.options.beforeUpload(this.file)) {
            throw new Error('Upload cancelled by beforeUpload callback');
        }
    }

    isFileTooLarge() {
        const maxSize = tool.parseSize(this.options.maxFileSize);
        return maxSize > 0 && this.file.size > maxSize;
    }

    async prepareUpload() {
        await this.calculateFileMD5();

        // 尝试从缓存恢复 uploadInfo 和续传记录
        const resumeRecord = await this.tryResumeFromCache();

        if (resumeRecord) {
            // 断点续传：SDK 已在 tryResumeFromCache 中初始化
            logger.log('[AWS Multipart] Resuming upload from cache', {
                uploadId: this.uploadRecord?.uploadId,
                completedParts: this.progressTracker?.completedParts?.size || 0,
            });
        } else {
            // 全新上传：获取 uploadInfo，初始化 SDK，创建 multipart upload
            await this.getUploadInfo();
            await this.initSDK(this.uploadInfo.region);
            await this.createMultipartUpload();
        }
    }

    /**
     * 尝试从缓存恢复上传信息和记录
     *
     * 注意：此方法会初始化 SDK 用于验证缓存有效性
     * - 如果缓存有效：SDK 保持初始化状态，返回缓存记录
     * - 如果缓存无效：清理 SDK 和 uploadInfo，返回 null
     *
     * @returns {Promise<Object|null>} 返回有效的缓存记录，或 null（表示需要全新上传）
     */
    async tryResumeFromCache() {
        const cachedRecord = await this.findCachedRecord();
        if (!cachedRecord || !cachedRecord.uploadInfo) {
            return null;
        }

        // 恢复 uploadInfo
        this.uploadInfo = cachedRecord.uploadInfo;

        // 初始化 SDK 用于验证缓存记录的有效性
        await this.initSDK(this.uploadInfo.region);
        const validationResult = await this.validateCachedUpload(cachedRecord);
        const validRecord = await this.handleValidationResult(validationResult, cachedRecord);

        // 如果验证失败，清理状态，返回 null 表示需要全新上传
        if (!validRecord) {
            this.uploadInfo = null;
            this.s3Client = null;
        }

        return validRecord;
    }

    async getUploadInfo() {
        const ext = this.file.name.split(/\./g).pop();
        const uploadObjData = await dataService.getUploadObj({
            id: this.options.signature.id,
            id_prefix: this.options.signature.id_prefix || 'teacher',
            media_type: this.options.signature.media_type || '',
            ext: ext,
        });

        if (!uploadObjData) {
            throw new Error('Failed to get upload object');
        }

        this.uploadInfo = uploadObjData;
        logger.log('[AWS Multipart] Upload info obtained:', {
            bucket: uploadObjData.bucket,
            key: uploadObjData.path,
            region: uploadObjData.region,
            resource_id: uploadObjData.resource_id,
            fileName: this.file.name,
            fileSize: this.file.size,
        });
    }

    async executeUpload() {
        await this.uploadAllParts();
        const result = await this.completeMultipartUpload();
        await this.handleSuccess(result);
    }

    async handleUploadError(error) {
        logger.errorAndLog('[AWS Multipart] Upload failed:', {
            error: error.message,
            errorType: ErrorHandler.classifyError(error),
            fileName: this.file?.name,
            taskId: this.taskId,
        });

        // 立即停止所有上传流程
        this.abortController.abort();

        this.cleanupTask();

        if (this.options.error) {
            this.options.error(error);
        }
    }

    async handleSuccess(result) {
        const resourceInfo = this.createResourceInfo(result);

        if (this.options.customCallback) {
            this.handleCustomCallback(resourceInfo);
        } else {
            await this.handleStandardCallback(resourceInfo);
        }
    }

    createResourceInfo(result) {
        const sourcePath = replaceAWSPath(decodeURIComponent(result.Location));
        return {
            resource_id: this.uploadInfo.resource_id,
            path: sourcePath,
        };
    }

    handleCustomCallback(resourceInfo) {
        if (isFunction(this.options.customCallback)) {
            this.options.customCallback(resourceInfo);
        }
        this.finishUpload(resourceInfo);
    }

    async handleStandardCallback(resourceInfo) {
        const mediaType = this.options.signature.media_type;

        if (!hasResourceCallback(mediaType)) {
            this.finishUpload(resourceInfo);
            return;
        }

        const uploadCallbackData = this.createUploadCallbackData(resourceInfo.path);

        if (this.options.syncUploadCallback) {
            await this.handleSyncCallback(uploadCallbackData, resourceInfo);
        } else {
            this.handleAsyncCallback(uploadCallbackData, resourceInfo);
        }
    }

    createUploadCallbackData(sourcePath) {
        return {
            media_type: this.options.signature.media_type,
            resource_id: this.uploadInfo.resource_id,
            file_name: this.file.name,
            file_size: this.file.size,
            path: sourcePath,
            ext: this.file.name.split(/\./g).pop(),
            extend_info: this.options.extend_info || '',
            sp: 2,
        };
    }

    async handleSyncCallback(uploadCallbackData, resourceInfo) {
        try {
            await dataService.uploadCallback(uploadCallbackData);
        } catch (error) {
            logger.errorAndLog('[AWS Multipart] uploadCallback error:', error);
            resourceInfo.uploadCallbackError = error;
        }
        this.finishUpload(resourceInfo);
    }

    handleAsyncCallback(uploadCallbackData, resourceInfo) {
        dataService.uploadCallback(uploadCallbackData);
        this.finishUpload(resourceInfo);
    }

    finishUpload(resourceInfo) {
        this.cleanupTask();
        if (this.options.success) {
            this.options.success(null, resourceInfo);
        }
    }
}

module.exports = {
    AwsMultipartUploader,
    taskManager,
};
